import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { setCartTotals, setCFDScreen, setPaymentData } from '../ActionCreators'
import { CFD_SCREEN } from '../Reducer'
import {
  bridge_setCurrentOrder,
  bridge_updateTransactionLifecycle,
  sendExecuteSalesRequest,
  sendOrderUpdate,
  sendUserPaymentProgress,
} from '../../VNAndroidSDK/bridgeCalls/VNWebSDKDataSend'
import { CFD_EVENTS, CFD_POS_PAYMENT_FLOWS, USER_PROGRESS } from '../Enums'
import { getDeviceSerial } from '../../utils/deviceData'
import { centsToDollars } from '../../utils/formatters'
import { invoiceNumber } from '../../utils/orderNumbers'
import { getTransactionBrandingConfig } from '../../utils/buildSettings'
import { getCanExecuteSalesRequest, getCartTotals, getCurrentPaymentFlow, getIsPaired } from '../Selectors'
import { VNCFD_TRANSACTION_RESULT } from '../ActionTypes'
import { setCFDPairedDialogOpen } from '../../VNDialogs/ActionCreators'

export const useVnPayment = () => {
  const dispatch = useDispatch()
  const cartTotals = useSelector((state) => getCartTotals(state))
  const isCFDPaired = useSelector((state) => getIsPaired(state))
  const currentPaymentFlow = useSelector((state) => getCurrentPaymentFlow(state))
  const canExecuteSalesRequest = useSelector((state) => getCanExecuteSalesRequest(state))

  const freeOrder = useCallback(
    (paymentData) => {
      dispatch({
        type: VNCFD_TRANSACTION_RESULT,
        data: { amount: paymentData.amount },
      })

      sendUserPaymentProgress({
        ...USER_PROGRESS.CARD_PAYMENT.PAYMENT_FINISHED.VIEWING_RECEIPT,
        ...{ approvedOffline: false, freeItem: true },
      })

      dispatch(setCFDScreen(CFD_SCREEN.RECEIPT))
    },
    [dispatch],
  )

  const getTotalInCents = useCallback(
    (charityAmountInCents = 0) => {
      const total =
        cartTotals.get('tax') +
        cartTotals.get('subtotal') +
        cartTotals?.get('tip') +
        cartTotals.get('serviceCharge') +
        cartTotals.get('discount') +
        charityAmountInCents

      // this can occur if the discount has an amount and subtotal doesn't
      if (total <= 0) return 0

      return total
    },
    [cartTotals],
  )

  const handlePaymentClick = useCallback(
    (charityAmountInCents = 0) => {
      if (!isCFDPaired) {
        dispatch(setCFDPairedDialogOpen(true))
        return
      }

      const curInvoiceNumber = cartTotals?.get('invoiceNumber') || invoiceNumber(cartTotals?.get('orderNumber'))
      const totalInCents = getTotalInCents(charityAmountInCents)
      const tipInCents = cartTotals?.get('tip')
      let paymentData = {
        amount: totalInCents,
        tipAmount: tipInCents,
        charityAmountInCents,
        hRef: curInvoiceNumber,
        invoiceNumber: curInvoiceNumber,
        orderNumber: cartTotals?.get('orderNumber'),
        uuid: cartTotals?.get('uuid'),
        deviceSerial: getDeviceSerial(),
        ...getTransactionBrandingConfig(),
      }

      sendOrderUpdate(paymentData)
      paymentData.amount = centsToDollars(totalInCents)
      paymentData.tip = centsToDollars(tipInCents)

      if (cartTotals?.get('isFinalizingTip') === 'true') {
        sendUserPaymentProgress({
          ...USER_PROGRESS.CARD_PAYMENT.PAYMENT_FINISHED.VIEWING_RECEIPT,
          ...{ tabbed: true, tipInCents, charityAmountInCents, tenderData: cartTotals?.get('tenderData') },
        })
        return
      }

      if (totalInCents === 0) {
        freeOrder(paymentData)
        return
      }

      if (canExecuteSalesRequest === 'false') {
        sendUserPaymentProgress({ tipAmount: tipInCents || 0, charityAmountInCents })
        dispatch(setCFDScreen(CFD_SCREEN.PROCESSING))
        return
      }

      dispatch(setPaymentData(paymentData))
      dispatch(setCartTotals({ ...cartTotals.toObject(), charityAmountInCents }))

      if (currentPaymentFlow === CFD_POS_PAYMENT_FLOWS.SPLIT_PAY) {
        bridge_setCurrentOrder(tipInCents || 0, charityAmountInCents || 0)
        dispatch(setCFDScreen(CFD_SCREEN.SPLIT_PAY_WAITING_FOR_ATTENDANT))
        bridge_updateTransactionLifecycle(CFD_EVENTS.CUSTOMER_WAITING_FOR_ATTENDANT)
        return
      }

      sendExecuteSalesRequest(paymentData)
      sendUserPaymentProgress({ ...USER_PROGRESS.CARD_PAYMENT.PAYING_CARD, amount: paymentData.amount })
      dispatch(setCFDScreen(CFD_SCREEN.PROCESSING))
    },
    [isCFDPaired, cartTotals, canExecuteSalesRequest, currentPaymentFlow, getTotalInCents, freeOrder, dispatch],
  )

  return {
    getTotalInCents,
    handlePaymentClick,
  }
}
